<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { getLastMessage } from 'dashboard/helper/conversationHelper';

const store = useStore();
const route = useRoute();
const router = useRouter();
const currentChat = computed(() => store.getters.getSelectedChat);
const pipLabels = {
  open: 'Abrir painel flutuante',
  title: 'Painel flutuante da Zoraxy',
  readOnly: 'Notificações · somente leitura',
  minimize: 'Minimizar painel flutuante',
  restore: 'Restaurar painel flutuante',
  close: 'Fechar painel flutuante',
  openConversation: 'Abrir conversa para responder',
};

const WEB_PIP_WIDTH = 320;
const WEB_PIP_HEIGHT = 520;
const WEB_PIP_TAB_WIDTH = 40;
const WEB_PIP_TAB_HEIGHT = 112;
const WEB_PIP_EDGE_SNAP_THRESHOLD = 18;
const WEB_PIP_EDGE_PADDING = 0;

const isPipSupported = ref(false);
const isPipEntering = ref(false);
const isNativePanelOpen = ref(false);
const nativePanelConversationId = ref('');
const isMobileViewport = ref(false);
const isWebPipOpen = ref(false);
const isWebPipMinimized = ref(false);
const webPipUrl = ref('');
const webPipPosition = ref({ right: 12, bottom: 12 });
const webPipEdge = ref('right');
let webPipWindow = null;
let webPipDrag = null;
let webPipSuppressClick = false;

const isWebPipEmbedded =
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).get('zoraxy_pip') === '1';

const isHorizontalWebPipTab = computed(() =>
  ['top', 'bottom'].includes(webPipEdge.value)
);
const webPipTabWidth = computed(() =>
  isHorizontalWebPipTab.value ? WEB_PIP_TAB_HEIGHT : WEB_PIP_TAB_WIDTH
);
const webPipTabHeight = computed(() =>
  isHorizontalWebPipTab.value ? WEB_PIP_TAB_WIDTH : WEB_PIP_TAB_HEIGHT
);
const webPipCurrentWidth = computed(() =>
  isWebPipMinimized.value
    ? webPipTabWidth.value
    : Math.min(WEB_PIP_WIDTH, window.innerWidth)
);
const webPipCurrentHeight = computed(() =>
  isWebPipMinimized.value
    ? webPipTabHeight.value
    : Math.min(WEB_PIP_HEIGHT, window.innerHeight)
);

const getFlutterBridge = () => {
  if (typeof window === 'undefined') return null;
  const bridge = window.flutter_inappwebview;
  if (!bridge?.callHandler) return null;
  return (...args) => bridge.callHandler(...args);
};

const getCurrentRouteUrl = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

const getPanelPathname = () => {
  const { pathname } = window.location;
  const accountConversationMatch = pathname.match(
    /^(\/app\/accounts\/[^/]+)\/conversations\/[^/]+$/
  );

  if (accountConversationMatch) {
    return `${accountConversationMatch[1]}/dashboard`;
  }

  return pathname.replace(/\/conversations\/[^/]+$/, '');
};

const getPanelRouteUrl = () => {
  const url = new URL(window.location.href);
  url.pathname = getPanelPathname();
  url.searchParams.delete('zoraxy_pip');
  url.searchParams.delete('messageId');
  return `${url.pathname}${url.search}${url.hash}`;
};

const getWebPipUrl = () => {
  const url = new URL(window.location.href);
  url.pathname = getPanelPathname();
  url.searchParams.set('zoraxy_pip', '1');
  url.searchParams.delete('messageId');
  return url.toString();
};

const getConversationRoute = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('zoraxy_pip');
  return `${url.pathname}${url.search}${url.hash}`;
};

const getConversationSnapshot = () => {
  const chat = currentChat.value;
  const sender = chat?.meta?.sender || {};
  const safeChat = {
    ...chat,
    messages: chat?.messages || [],
  };
  const lastMessage = getLastMessage(safeChat);
  const preview = String(lastMessage?.content || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return {
    conversationId: String(chat?.display_id || ''),
    conversationRoute: getConversationRoute(),
    conversationName:
      sender.name || sender.phone_number || pipLabels.title,
    lastMessagePreview: preview,
    avatarUrl: sender.thumbnail || '',
    count: Number(chat?.unread_count || 0),
    connectionState: 'connected',
  };
};

const syncNativePanelSnapshot = async () => {
  if (!isNativePanelOpen.value) return;
  const snapshot = getConversationSnapshot();
  if (
    !snapshot.conversationId ||
    snapshot.conversationId !== nativePanelConversationId.value
  ) {
    return;
  }

  const callHandler = getFlutterBridge();
  if (!callHandler) return;
  try {
    await callHandler('FlutterApp', 'update_picture_in_picture', snapshot);
  } catch {
    // The panel keeps its last known snapshot if the native bridge is closing.
  }
};

const updateMobileViewport = () => {
  isMobileViewport.value = window.matchMedia('(max-width: 767px)').matches;
};

const isConversationRouteUrl = routeUrl => {
  if (!routeUrl) return false;
  const pathname = routeUrl.split(/[?#]/)[0];
  return (
    /\/app\/accounts\/[^/]+\/conversations\/[^/]+$/.test(pathname) ||
    /\/conversations\/[^/]+$/.test(pathname)
  );
};

const applyReadOnlyToIframe = iframe => {
  try {
    const style = iframe.contentDocument.createElement('style');
    style.textContent = `
      .reply-box,
      .acl-reply-top-out,
      .acl-reply-banners,
      .aceleres-start-gate {
        display: none !important;
      }
      input,
      textarea,
      [contenteditable='true'] {
        pointer-events: none !important;
        user-select: none !important;
      }
    `;
    iframe.contentDocument.head.appendChild(style);
  } catch {
    // The embedded conversation remains usable if the browser blocks access.
  }
};

const getPointerPosition = event => ({
  x: event.clientX,
  y: event.clientY,
});

const clampWebPipPosition = () => {
  const maxRight = Math.max(
    WEB_PIP_EDGE_PADDING,
    window.innerWidth - webPipCurrentWidth.value - WEB_PIP_EDGE_PADDING
  );
  const maxBottom = Math.max(
    WEB_PIP_EDGE_PADDING,
    window.innerHeight - webPipCurrentHeight.value - WEB_PIP_EDGE_PADDING
  );

  webPipPosition.value = {
    right: Math.min(
      maxRight,
      Math.max(WEB_PIP_EDGE_PADDING, webPipPosition.value.right)
    ),
    bottom: Math.min(
      maxBottom,
      Math.max(WEB_PIP_EDGE_PADDING, webPipPosition.value.bottom)
    ),
  };
};

const moveWebPip = event => {
  if (!webPipDrag) return;
  webPipDrag.moved = true;
  const pointer = getPointerPosition(event);
  const maxRight = Math.max(
    WEB_PIP_EDGE_PADDING,
    window.innerWidth - webPipCurrentWidth.value - WEB_PIP_EDGE_PADDING
  );
  const maxBottom = Math.max(
    WEB_PIP_EDGE_PADDING,
    window.innerHeight - webPipCurrentHeight.value - WEB_PIP_EDGE_PADDING
  );

  webPipPosition.value = {
    right: Math.min(
      maxRight,
      Math.max(
        WEB_PIP_EDGE_PADDING,
        webPipDrag.right - (pointer.x - webPipDrag.x)
      )
    ),
    bottom: Math.min(
      maxBottom,
      Math.max(
        WEB_PIP_EDGE_PADDING,
        webPipDrag.bottom - (pointer.y - webPipDrag.y)
      )
    ),
  };
};

const snapWebPipToEdge = () => {
  if (!isWebPipMinimized.value) return;

  const sideMaxRight = Math.max(0, window.innerWidth - WEB_PIP_TAB_WIDTH);
  const sideMaxBottom = Math.max(0, window.innerHeight - WEB_PIP_TAB_HEIGHT);
  const horizontalMaxRight = Math.max(
    0,
    window.innerWidth - WEB_PIP_TAB_HEIGHT
  );
  const horizontalMaxBottom = Math.max(
    0,
    window.innerHeight - WEB_PIP_TAB_WIDTH
  );
  const currentRight = webPipPosition.value.right;
  const currentBottom = webPipPosition.value.bottom;
  const currentLeft =
    window.innerWidth - webPipCurrentWidth.value - currentRight;
  const currentTop =
    window.innerHeight - webPipCurrentHeight.value - currentBottom;
  const sideClampedBottom = Math.min(sideMaxBottom, Math.max(0, currentBottom));
  const horizontalClampedRight = Math.min(
    horizontalMaxRight,
    Math.max(0, currentRight)
  );
  const edges = [
    {
      distance: currentLeft,
      edge: 'left',
      right: sideMaxRight,
      bottom: sideClampedBottom,
    },
    {
      distance: currentRight,
      edge: 'right',
      right: 0,
      bottom: sideClampedBottom,
    },
    {
      distance: currentTop,
      edge: 'top',
      right: horizontalClampedRight,
      bottom: horizontalMaxBottom,
    },
    {
      distance: currentBottom,
      edge: 'bottom',
      right: horizontalClampedRight,
      bottom: 0,
    },
  ];
  const nearestEdge = edges.reduce((nearest, edge) =>
    edge.distance < nearest.distance ? edge : nearest
  );

  webPipPosition.value = {
    right: Math.max(0, nearestEdge.right),
    bottom: Math.max(0, nearestEdge.bottom),
  };
  webPipEdge.value = nearestEdge.edge;
};

const minimizeWebPipIfDocked = () => {
  if (isWebPipMinimized.value) {
    snapWebPipToEdge();
    return;
  }

  const pipWidth = webPipCurrentWidth.value;
  const pipHeight = webPipCurrentHeight.value;
  const currentRight = webPipPosition.value.right;
  const currentBottom = webPipPosition.value.bottom;
  const currentLeft = window.innerWidth - pipWidth - currentRight;
  const currentTop = window.innerHeight - pipHeight - currentBottom;
  const nearestDistance = Math.min(
    currentLeft,
    currentRight,
    currentTop,
    currentBottom
  );

  if (nearestDistance > WEB_PIP_EDGE_SNAP_THRESHOLD) return;
  isWebPipMinimized.value = true;
  snapWebPipToEdge();
};

const stopWebPipDrag = () => {
  const moved = webPipDrag?.moved === true;
  webPipDrag = null;
  window.removeEventListener('pointermove', moveWebPip);
  window.removeEventListener('pointerup', stopWebPipDrag);
  window.removeEventListener('pointercancel', stopWebPipDrag);
  if (moved) {
    minimizeWebPipIfDocked();
    webPipSuppressClick = true;
    window.setTimeout(() => {
      webPipSuppressClick = false;
    }, 150);
  }
};

const startWebPipDrag = event => {
  const isDragSurface = event.target.closest('[data-pip-drag-surface]');
  if (
    !isWebPipMinimized.value &&
    event.target.closest('button') &&
    !isDragSurface
  ) {
    return;
  }
  const pointer = getPointerPosition(event);
  if (isWebPipMinimized.value) webPipEdge.value = null;
  event.currentTarget?.setPointerCapture?.(event.pointerId);
  webPipDrag = {
    x: pointer.x,
    y: pointer.y,
    right: webPipPosition.value.right,
    bottom: webPipPosition.value.bottom,
    moved: false,
  };
  window.addEventListener('pointermove', moveWebPip);
  window.addEventListener('pointerup', stopWebPipDrag);
  window.addEventListener('pointercancel', stopWebPipDrag);
};

const handleWebPipPointerDown = event => {
  if (
    isWebPipMinimized.value ||
    event.target.closest('[data-pip-drag-surface]')
  ) {
    startWebPipDrag(event);
  }
};

const openWebPipFallback = () => {
  webPipUrl.value = getWebPipUrl();
  isWebPipMinimized.value = false;
  isWebPipOpen.value = true;
};

function closeWebPipFallback() {
  stopWebPipDrag();
  isWebPipOpen.value = false;
  isWebPipMinimized.value = false;
  webPipEdge.value = 'right';
  webPipUrl.value = '';
}

async function openConversationRouteFromPip(routeToOpen) {
  if (webPipSuppressClick) {
    webPipSuppressClick = false;
    return;
  }

  if (isWebPipEmbedded) {
    const callHandler = getFlutterBridge();
    if (callHandler) {
      await callHandler('FlutterApp', 'open_conversation_from_pip', {
        route: routeToOpen,
      });
      return;
    }
    window.parent?.postMessage(
      {
        type: 'zoraxy:pip:conversation-selected',
        route: routeToOpen,
      },
      window.location.origin
    );
    return;
  }

  if (webPipWindow && !webPipWindow.closed) {
    webPipWindow.close();
    webPipWindow = null;
  }
  if (isConversationRouteUrl(routeToOpen)) {
    webPipUrl.value = getWebPipUrl();
    isWebPipOpen.value = true;
    isWebPipMinimized.value = true;
    webPipEdge.value = 'right';
    webPipPosition.value = {
      right: 0,
      bottom: Math.max(72, webPipPosition.value.bottom),
    };
    snapWebPipToEdge();
    await router.push(routeToOpen);
  } else {
    closeWebPipFallback();
  }
  window.focus();
}

const handleWebPipMessage = event => {
  if (event.origin !== window.location.origin) return;
  const { data } = event;
  if (data?.type !== 'zoraxy:pip:conversation-selected' || !data.route) return;
  openConversationRouteFromPip(data.route);
};

const requestDocumentPictureInPicture = async () => {
  const documentPip = window.documentPictureInPicture;
  if (!documentPip?.requestWindow) return false;

  if (webPipWindow && !webPipWindow.closed) {
    webPipWindow.focus();
    return true;
  }

  try {
    const pipWindow = await documentPip.requestWindow({
      width: WEB_PIP_WIDTH,
      height: WEB_PIP_HEIGHT,
    });
    const iframe = pipWindow.document.createElement('iframe');

    pipWindow.document.title = pipLabels.title;
    pipWindow.document.body.style.cssText =
      'margin:0; height:100vh; overflow:hidden; position:relative; background:#fff;';
    iframe.src = getWebPipUrl();
    iframe.title = pipLabels.title;
    iframe.allow = 'clipboard-read; clipboard-write; microphone; camera';
    iframe.style.cssText = 'width:100%; height:100%; border:0; display:block;';
    iframe.addEventListener('load', () => applyReadOnlyToIframe(iframe));
    pipWindow.document.body.appendChild(iframe);
    webPipWindow = pipWindow;
    pipWindow.addEventListener('message', handleWebPipMessage);
    pipWindow.addEventListener('pagehide', () => {
      if (webPipWindow === pipWindow) webPipWindow = null;
    });
    return true;
  } catch {
    return false;
  }
};

const toggleWebPipMinimized = () => {
  if (webPipSuppressClick) return;
  stopWebPipDrag();
  isWebPipMinimized.value = !isWebPipMinimized.value;
  if (isWebPipMinimized.value) {
    snapWebPipToEdge();
  } else {
    webPipUrl.value = getWebPipUrl();
    webPipEdge.value = 'right';
    clampWebPipPosition();
  }
};

const handleNativePipChange = event => {
  isNativePanelOpen.value = event.detail?.active === true;
  if (!isNativePanelOpen.value) nativePanelConversationId.value = '';
  const shouldUseReadOnlyLayout =
    isWebPipEmbedded ||
    (event.detail?.active === true &&
      event.detail?.mode !== 'in_app_floating_panel');
  document.body.classList.toggle(
    'zoraxy-pip-read-only',
    shouldUseReadOnlyLayout
  );
};

const installPipReadOnlyStyle = () => {
  if (document.getElementById('zoraxy-pip-read-only-style')) return;
  const style = document.createElement('style');
  style.id = 'zoraxy-pip-read-only-style';
  style.textContent = `
    .zoraxy-pip-read-only .reply-box,
    .zoraxy-pip-read-only .acl-reply-top-out,
    .zoraxy-pip-read-only .acl-reply-banners,
    .zoraxy-pip-read-only .aceleres-start-gate {
      display: none !important;
    }
    .zoraxy-pip-read-only .dashboard-app,
    .zoraxy-pip-read-only .app-content,
    .zoraxy-pip-read-only .conversations-list-wrap {
      width: 100% !important;
      max-width: none !important;
    }
    .zoraxy-pip-read-only .sidebar,
    .zoraxy-pip-read-only .app-sidebar,
    .zoraxy-pip-read-only .main-sidebar,
    .zoraxy-pip-read-only [data-mobile-sidebar-launcher],
    .zoraxy-pip-read-only .acl-chatlist-subtitle,
    .zoraxy-pip-read-only .acl-quick-search-wrap,
    .zoraxy-pip-read-only .inbox-filter-chips,
    .zoraxy-pip-read-only .acl-archived-row,
    .zoraxy-pip-read-only .acl-conv-list-resize-handle,
    .zoraxy-pip-read-only .conversation-panel,
    .zoraxy-pip-read-only .conversation-details-wrap,
    .zoraxy-pip-read-only .conversation-sidebar,
    .zoraxy-pip-read-only .acl-chatlist-actions > *:not(.acl-pip-keep-in-embedded) {
      display: none !important;
    }
    .zoraxy-pip-read-only .acl-chatlist-actions {
      display: none !important;
    }
    .zoraxy-pip-read-only .acl-chatlist-header {
      height: 48px !important;
      padding: 8px 10px !important;
      border-bottom: 1px solid rgb(226, 232, 240) !important;
    }
    .zoraxy-pip-read-only .acl-chatlist-title {
      font-size: 16px !important;
      line-height: 1.2 !important;
    }
    .zoraxy-pip-read-only .acl-dashboard-flow {
      min-height: 0 !important;
    }
    .zoraxy-pip-read-only .acl-filters-bar {
      padding: 6px 6px 4px !important;
      border-bottom: 1px solid rgb(226, 232, 240) !important;
    }
    .zoraxy-pip-read-only .acl-filters-bar > *:not(:first-child) {
      display: none !important;
    }
    .zoraxy-pip-read-only .conversations-list {
      margin-top: 0 !important;
    }
    .zoraxy-pip-read-only input,
    .zoraxy-pip-read-only textarea,
    .zoraxy-pip-read-only [contenteditable='true'] {
      pointer-events: none !important;
      user-select: none !important;
    }
  `;
  document.head.appendChild(style);
};

const checkPictureInPictureSupport = async () => {
  if (isWebPipEmbedded || !isMobileViewport.value) {
    isPipSupported.value = false;
    return;
  }
  const callHandler = getFlutterBridge();
  if (!callHandler) {
    isPipSupported.value = true;
    return;
  }

  try {
    const capability = await callHandler(
      'FlutterApp',
      'get_picture_in_picture_capability'
    );
    isPipSupported.value = capability?.supported === true;
  } catch {
    isPipSupported.value = false;
  }
};

const handleViewportChange = () => {
  updateMobileViewport();
  if (!isMobileViewport.value && isWebPipOpen.value) closeWebPipFallback();
  checkPictureInPictureSupport();
};

const enterPictureInPicture = async () => {
  if (isWebPipEmbedded || !isMobileViewport.value || isPipEntering.value) {
    return;
  }

  const callHandler = getFlutterBridge();
  isPipEntering.value = true;
  try {
    if (callHandler) {
      const snapshot = getConversationSnapshot();
      const result = await callHandler(
        'FlutterApp',
        'enter_picture_in_picture',
        {
          route: getPanelRouteUrl() || getCurrentRouteUrl(),
          ...snapshot,
        }
      );
      if (result?.entered === true) {
        isNativePanelOpen.value = true;
        nativePanelConversationId.value = snapshot.conversationId;
        return;
      }
    } else if (await requestDocumentPictureInPicture()) {
      return;
    }

    openWebPipFallback();
  } finally {
    isPipEntering.value = false;
  }
};

let lastEmbeddedConversationRoute = '';

const notifyEmbeddedConversationSelection = () => {
  if (!isWebPipEmbedded) return;
  const routeToOpen = getConversationRoute();
  if (
    !isConversationRouteUrl(routeToOpen) ||
    routeToOpen === lastEmbeddedConversationRoute
  ) {
    return;
  }

  lastEmbeddedConversationRoute = routeToOpen;
  openConversationRouteFromPip(routeToOpen);
};

onMounted(() => {
  handleViewportChange();
  installPipReadOnlyStyle();
  handleNativePipChange({ detail: { active: isWebPipEmbedded } });
  window.addEventListener('zoraxy:pipchange', handleNativePipChange);
  window.addEventListener('resize', handleViewportChange);
  window.addEventListener('message', handleWebPipMessage);
  notifyEmbeddedConversationSelection();
});

onBeforeUnmount(() => {
  stopWebPipDrag();
  window.removeEventListener('zoraxy:pipchange', handleNativePipChange);
  window.removeEventListener('resize', handleViewportChange);
  window.removeEventListener('message', handleWebPipMessage);
  if (isWebPipEmbedded) document.body.classList.remove('zoraxy-pip-read-only');
  if (webPipWindow && !webPipWindow.closed) webPipWindow.close();
});

watch(
  () => route.fullPath,
  () => notifyEmbeddedConversationSelection()
);

watch(getConversationSnapshot, () => syncNativePanelSnapshot(), {
  deep: true,
});
</script>

<template>
  <NextButton
    v-if="isMobileViewport && isPipSupported"
    v-tooltip="pipLabels.open"
    ghost
    slate
    md
    :is-loading="isPipEntering"
    icon="i-lucide-picture-in-picture size-6"
    data-zoraxy-pip-button
    :aria-label="pipLabels.open"
    @click.stop="enterPictureInPicture"
  />

  <div
    v-if="isMobileViewport && isWebPipOpen"
    class="acl-zoraxy-web-pip fixed z-[1000] flex overflow-hidden border border-n-weak bg-white shadow-2xl"
    :class="
      isWebPipMinimized
        ? [
            webPipEdge === 'left' ? 'rounded-r-xl rounded-l-none' : '',
            webPipEdge === 'right' ? 'rounded-l-xl rounded-r-none' : '',
            webPipEdge === 'top' ? 'rounded-b-xl rounded-t-none' : '',
            webPipEdge === 'bottom' ? 'rounded-t-xl rounded-b-none' : '',
          ]
        : 'flex-col rounded-xl'
    "
    :style="{
      left: isWebPipMinimized && webPipEdge === 'left' ? '0px' : 'auto',
      right:
        isWebPipMinimized && webPipEdge === 'right'
          ? '0px'
          : `${webPipPosition.right}px`,
      top: isWebPipMinimized && webPipEdge === 'top' ? '0px' : 'auto',
      bottom:
        isWebPipMinimized && webPipEdge === 'bottom'
          ? '0px'
          : `${webPipPosition.bottom}px`,
      width: `${webPipCurrentWidth}px`,
      height: `${webPipCurrentHeight}px`,
    }"
    @click.stop
    @pointerdown="handleWebPipPointerDown"
  >
    <button
      v-if="isWebPipMinimized"
      type="button"
      class="flex size-full touch-none items-center justify-center text-n-brand"
      :aria-label="pipLabels.restore"
      @click="toggleWebPipMinimized"
    >
      <span class="i-lucide-message-circle size-6" />
    </button>
    <template v-else>
      <div
        class="flex h-9 flex-shrink-0 touch-none select-none items-center justify-between border-b border-n-weak px-2 cursor-move"
        data-pip-drag-surface
        @pointerdown="startWebPipDrag"
      >
        <span class="text-[11px] font-semibold text-n-slate-12">
          {{ pipLabels.readOnly }}
        </span>
        <div class="flex items-center gap-1">
          <NextButton
            v-tooltip="pipLabels.minimize"
            ghost
            slate
            xs
            icon="i-lucide-minus"
            :aria-label="pipLabels.minimize"
            @click="toggleWebPipMinimized"
          />
          <NextButton
            v-tooltip="pipLabels.close"
            ghost
            slate
            xs
            icon="i-lucide-x"
            :aria-label="pipLabels.close"
            @click="closeWebPipFallback"
          />
        </div>
      </div>
      <div class="relative min-h-0 flex-1">
        <iframe
          :src="webPipUrl"
          :title="pipLabels.title"
          class="absolute inset-0 h-full w-full border-0"
          allow="clipboard-read; clipboard-write; microphone; camera"
          @load="applyReadOnlyToIframe"
        />
      </div>
    </template>
  </div>
</template>

<style scoped>
.acl-zoraxy-web-pip {
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
}
</style>
